import { useEffect, useState } from "react";
import { useParams } from "@tanstack/react-router";
import { Banner } from "@praesto/ui-kit";
import { useCore } from "../lib/core";
import { useCan } from "../lib/useCan";
import { subscribeTrialEnd } from "../lib/trial-end";
import { TEAM_CONTACT_EMAIL, formatTrialDate, trialState } from "./trial";

/**
 * Free-trial notice (C-197), for the tenant owner only.
 *
 * While the trial runs: one quiet line with its end date, which the owner may
 * close for the session. From the end date onward: the trial is over and the
 * way to continue is to write to the team. That line cannot be closed — it is
 * a state that lasts until someone acts on it — but it suspends nothing: the
 * account keeps working, and suspension stays a manual step in the console.
 * No email is sent to the customer.
 */
export function TrialNotice({ now = () => new Date() }: { now?: () => Date }) {
  const core = useCore();
  const t = core.i18n.t;
  const isOwner = useCan("manage", "hotel");
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug?: string };
  const [endsAt, setEndsAt] = useState<Date | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isOwner || !tenantSlug) return;
    return subscribeTrialEnd(tenantSlug, setEndsAt);
  }, [isOwner, tenantSlug]);

  if (!isOwner) return null;
  const state = trialState(endsAt, now());
  if (state.kind === "none") return null;
  const date = formatTrialDate(state.endsAt, core.i18n.locale());

  if (state.kind === "active") {
    if (dismissed) return null;
    return (
      <Banner tone="info" onDismiss={() => setDismissed(true)}>
        {t("trial.active.message", { date })}
      </Banner>
    );
  }

  const subject = encodeURIComponent(t("trial.ended.mailSubject"));
  return (
    <Banner tone="warning">
      {t("trial.ended.message", { date })}{" "}
      <a
        href={`mailto:${TEAM_CONTACT_EMAIL}?subject=${subject}`}
        className="underline underline-offset-2 whitespace-nowrap"
      >
        {TEAM_CONTACT_EMAIL}
      </a>
    </Banner>
  );
}
